// Pure formatting helpers for ActivityFeed rows. No React, no state —
// safe to call from anywhere. `t` is passed in so the caller controls
// the i18n context (server-rendered fallback strings or live useT()).

type TFn = (k: string, vars?: Record<string, string | number>) => string;

export function formatUSD(num: number | string | undefined | null): string {
  const n = Number(num) || 0;
  if (n === 0) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  if (n < 10) return `$${n.toFixed(2)}`;
  return `$${Math.round(n)}`;
}

export function timeAgo(date: string, t: TFn): string {
  // Compact "5h ago" form. The original long-form ("5 hours ago") was
  // dropped when it duplicated the group separator above each card; once
  // the separator was removed, "ago" came back to read naturally. Format
  // stays language-agnostic for the number + unit, "ago" is a hardcoded
  // English suffix — short enough that it doesn't clash with i18n locales
  // for this surface.
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return t("time.justNow");
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

// Strip emoji characters from text (backend headlines include 📈📉 etc.)
export const stripEmoji = (t: string) => t.replace(/\p{Emoji_Presentation}|\p{Extended_Pictographic}/gu, '').trim();

/**
 * Display-safe name for a trader. If the input looks like a raw EVM address
 * (0x + 40 hex chars), truncate to "0x5966...F804" form. Otherwise return
 * as-is (real usernames, ENS names, custom wallet labels, etc.). Centralises
 * the truncation so rows + copy buttons + hover cards don't have to
 * re-implement it.
 */
export function displayTraderName(name: string | null | undefined): string {
  if (!name) return "Unknown";
  if (/^0x[a-fA-F0-9]{40}$/.test(name)) {
    return `${name.slice(0, 6)}...${name.slice(-4)}`;
  }
  return name;
}

/**
 * Abbreviate outcome labels for the YES/NO chip pair under a market
 * embed card. Pika reported (2026-05-22) seeing the full
 * "Felix Gill" / "Kyrian Jacquet" names on a Roland Garros card —
 * the chips are only ~70-90px wide so anything longer truncates with
 * an ellipsis. Rules:
 *   - "Yes" / "No" untouched (already short, semantically loaded).
 *   - ≤4 chars → uppercase as-is.
 *   - Multi-word ("Felix Gill", "AC Milan") → initials of each word,
 *     up to 3 chars.
 *   - Single-word longer than 4 ("Knicks", "Lakers") → first 3 chars
 *     uppercased.
 */
export function abbreviateOutcomeLabel(label: string | undefined | null): string {
  if (!label) return "";
  const trimmed = label.trim();
  if (!trimmed) return "";
  if (/^(yes|no)$/i.test(trimmed)) return trimmed;
  if (trimmed.length <= 4) return trimmed.toUpperCase();
  const words = trimmed.split(/\s+/).filter((w) => w.length > 0);
  if (words.length > 1) {
    return words.map((w) => w[0]!).join("").slice(0, 3).toUpperCase();
  }
  return trimmed.slice(0, 3).toUpperCase();
}

export function getTimeGroup(date: string, t: TFn): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  if (diffMin < 5) return t("activity.group.justNow");
  if (diffMin < 60) return t("activity.group.minutesAgo");
  if (diffHours < 2) return t("activity.group.oneHourAgo");
  if (diffHours < 24) return t("activity.group.hoursAgo", { n: diffHours });
  const today = new Date();
  const eventDate = new Date(date);
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const eventDayStart = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()).getTime();
  const daysDiff = Math.floor((todayStart - eventDayStart) / 86400000);
  if (daysDiff === 1) return t("activity.group.yesterday");
  if (daysDiff < 7) return t("activity.group.daysAgo", { n: daysDiff });
  if (daysDiff < 14) return t("activity.group.lastWeek");
  return t("activity.group.older");
}
